import {
  conditionalLoad,
  conditionalFastLoad,
  conditionalLoadHeavy,
  inject,
  fastLoad,
} from '@/shared/AsyncLoad';
import { cactusEvents } from '@/shared/AsyncLoad/scripts/cactusEvents';
import { customInjectedScript } from '@/shared/AsyncLoad/scripts/customInjectedScript';
import {
  facebookScript,
  gtmScript,
  kwaiScript,
  taboolaScript,
  cactusCache,
} from '@/shared/AsyncLoad/scripts/trackers';
import {
  altenarScript,
  betbyScript,
  liveChat,
  octaDeskScript,
  zenDeskScript,
  smarticoScript,
  softswissScript,
} from '@/shared/AsyncLoad/scripts/vendors';
import {
  antillephoneScript,
  anjsealScript,
} from '@/shared/AsyncLoad/scripts/seals';

export default defineNuxtPlugin({
  name: 'asyncLoad',

  setup() {
    const { primarySealId, primarySealIsAnjseal, primarySealIsAntillephone } =
      useLicenses();

    const { appName, betBy, altenar, smartico, BUILD_UNIQUE_ID, gtmOverride } =
      useRuntimeConfig().public;

    const {
      isAltenarEnabled,
      isAltenarTestModeEnabled,
      isBetByTestModeEnabled,
      isBetByEnabled,
      isTaboolaEnabled,
      isKwaiPixelEnabled,
      isDisableBrandGtm,
    } = useFeatureFlagsEnv();

    const {
      isZenDeskActive,
      chatTokenZendesk,
      isLiveChatActive,
      chatTokenLiveChat,
      isOctaDeskActive,
      chatTokenOctaDesk,
    } = useLiveSupport();

    const { libraryUrl: betByLibraryUrl } = betBy;
    const { libraryUrl: altenarLibraryUrl } = altenar;

    const {
      libraryUrl: smarticoLibraryUrl,
      brandId: smarticoBrandId,
      labelId: smarticoLabelId,
    } = smartico;

    const { parameters } = useParameters();
    const gtmId = gtmOverride || parameters.value?.gtm_id || '';

    const activeGtm = computed(() => {
      return !isDisableBrandGtm.value && !!gtmId;
    });

    const isSmarticoEnabled =
      !!smarticoLibraryUrl && !!smarticoBrandId && !!smarticoLabelId;

    const isUserLoggedIn = computed(() => !!useCookie('jwt_token').value);

    // Tracking de site de terceiros é desligado dentro do app (iOS e Android),
    // sempre — ver useAppTrackingPolicy (ENG-397). Fora do app (navegador),
    // comportamento inalterado.
    const { siteTrackingEnabled } = useAppTrackingPolicy();

    function injectScriptsIntoHead() {
      const onlyIsFirst = (script: string, name: string) => {
        return `
        window.cactusEventsInitLoaded = window.cactusEventsInitLoaded || {};
        if(!window.cactusEventsInitLoaded['${name}']){
          window.cactusEventsInitLoaded['${name}'] = true;
          ${script}
        }
      `;
      };

      const softswissLibraryUrl = `https://s3.eu-central-1.amazonaws.com/ignition.button/round-2/connector.js`;

      // Scripts essenciais/funcionais: provedores de jogos, CRM, chats e
      // selos regulatórios. Não são trackers de publicidade — injetam sempre.
      useHead({
        script: [
          fastLoad(
            onlyIsFirst(cactusCache(appName, BUILD_UNIQUE_ID), 'cacheCheck'),
          ),
          fastLoad(
            onlyIsFirst(softswissScript(softswissLibraryUrl), 'softswiss'),
          ),
          inject(cactusEvents),
          inject(customInjectedScript),
          conditionalLoadHeavy(
            isBetByEnabled.value || isBetByTestModeEnabled.value,
            betbyScript(betByLibraryUrl),
            8000, // BetBy é pesado, delay de 8s
          ),
          conditionalLoadHeavy(
            isAltenarEnabled.value || isAltenarTestModeEnabled.value,
            altenarScript(altenarLibraryUrl),
            10000, // Altenar é MUITO pesado (278KB), delay de 10s
          ),
          isUserLoggedIn.value
            ? conditionalFastLoad(
                isSmarticoEnabled,
                smarticoScript(smarticoLibraryUrl),
              )
            : conditionalLoad(
                isSmarticoEnabled,
                smarticoScript(smarticoLibraryUrl),
              ),
          conditionalLoad(
            isOctaDeskActive.value,
            octaDeskScript(chatTokenOctaDesk.value),
          ),
          conditionalLoad(
            isZenDeskActive.value,
            zenDeskScript(chatTokenZendesk.value),
          ),
          conditionalLoad(
            isLiveChatActive.value,
            liveChat(chatTokenLiveChat.value),
          ),
          conditionalLoad(
            primarySealIsAntillephone.value,
            antillephoneScript(primarySealId.value),
          ),
          conditionalLoad(
            primarySealIsAnjseal.value,
            anjsealScript(primarySealId.value),
          ),
        ],
      });

      // Pixels de marketing (GTM, Facebook, Kwai, Taboola): gravam cookies de
      // rastreamento. Dentro do app ficam SEMPRE off (separação app/site,
      // App Store 5.1.1(iv)); no navegador, injetam como antes.
      useHead({
        script: computed(() =>
          siteTrackingEnabled.value
            ? [
                conditionalLoadHeavy(
                  activeGtm.value,
                  onlyIsFirst(gtmScript(gtmId), 'gtm'),
                  8000,
                ),
                conditionalLoadHeavy(
                  !!parameters.value.pixel_id,
                  onlyIsFirst(facebookScript, 'facebook'),
                  8000,
                ),
                conditionalLoad(
                  isKwaiPixelEnabled.value,
                  onlyIsFirst(kwaiScript, 'kwai'),
                ),
                conditionalLoad(
                  isTaboolaEnabled.value,
                  onlyIsFirst(taboolaScript, 'taboola'),
                ),
              ]
            : [],
        ),
      });
    }

    return {
      provide: {
        asyncLoad: {
          injectScriptsIntoHead,
        },
      },
    };
  },

  hooks: {
    // You can directly register Nuxt app runtime hooks here
    'app:mounted'() {
      const { $asyncLoad } = useNuxtApp();

      $asyncLoad.injectScriptsIntoHead();
    },
  },
});
